import React from 'react';
import { cn } from '@/lib/utils';

export const DashRow: React.FC<{
  children: React.ReactNode;
  align?: 'stretch' | 'start';
}> = ({ children, align = 'stretch' }) => (
  <div
    className={cn(
      'flex flex-wrap gap-5',
      align === 'start' ? 'items-start' : 'items-stretch',
    )}
  >
    {children}
  </div>
);

export const DashGreeting: React.FC<{ date: string; name: string }> = ({
  date,
  name,
}) => (
  <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
    <span className="mr-auto flex flex-col gap-1.5">
      <span className="col-label text-faint">{date}</span>
      <span className="font-display text-[clamp(22px,2vw,28px)] font-extrabold leading-[1.1] tracking-[-.025em] text-white">
        Bem-vindo, {name}.
      </span>
    </span>
  </div>
);

/** Card padrão do dashboard: cabeçalho com título Barlow 16px caixa alta + subtítulo + ação à direita. */
export const DashCard: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  size?: 'wide' | 'narrow' | 'auto';
  bodyClassName?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, action, size = 'auto', bodyClassName, children }) => (
  <section
    className={cn(
      'flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card',
      size === 'wide' && 'flex-[2_1_560px]',
      size === 'narrow' && 'flex-[1_1_320px]',
    )}
  >
    <header className="flex items-center gap-3 border-b border-border px-6 py-5">
      <span className="mr-auto flex flex-col gap-[5px]">
        <span className="font-display text-base font-extrabold uppercase tracking-[-.02em] text-white">
          {title}
        </span>
        {subtitle && (
          <span className="text-[12.5px] text-muted-foreground">{subtitle}</span>
        )}
      </span>
      {action}
    </header>
    <div className={cn('flex flex-1 flex-col', bodyClassName)}>{children}</div>
  </section>
);

/** Link "Ver todos ›" em areia no cabeçalho de card. */
export const DashLink: React.FC<{
  onClick?: () => void;
  children: React.ReactNode;
  icon: React.ReactNode;
}> = ({ onClick, children, icon }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1.5 text-xs font-bold text-sand hover:opacity-85"
  >
    {children}
    {icon}
  </button>
);

/** Faixa única com 4 KPIs separados por hairline vertical (não são 4 cards soltos). */
export const KpiStrip: React.FC<{
  items: {
    label: string;
    value: string;
    note: string;
    noteTone?: 'olive' | 'sand' | 'muted';
    icon: React.ReactNode;
    iconTone?: 'olive' | 'sand' | 'danger';
  }[];
}> = ({ items }) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,230px),1fr))] overflow-hidden rounded-2xl border border-border bg-card">
    {items.map((k) => (
      <div
        key={k.label}
        className="-ml-px flex flex-col gap-3.5 border-l border-border px-6 py-[22px]"
      >
        <span className="flex items-center gap-2.5">
          <span
            className={cn(
              'flex size-[30px] shrink-0 items-center justify-center rounded-[9px] border border-border',
              k.iconTone === 'sand'
                ? 'text-sand'
                : k.iconTone === 'danger'
                  ? 'text-danger-soft'
                  : 'text-olive-icon',
            )}
          >
            {k.icon}
          </span>
          <span className="col-label text-muted-foreground">{k.label}</span>
        </span>
        <span className="flex items-baseline gap-2.5">
          <span className="font-display text-[34px] font-extrabold leading-none tracking-[-.035em] text-white">
            {k.value}
          </span>
          <span
            className={cn(
              'text-xs font-semibold',
              k.noteTone === 'olive'
                ? 'text-olive-ink'
                : k.noteTone === 'sand'
                  ? 'text-sand'
                  : 'text-muted-foreground',
            )}
          >
            {k.note}
          </span>
        </span>
      </div>
    ))}
  </div>
);

/** Pílula de status 24px, caixa alta, com ponto. Sempre `whitespace-nowrap`. */
export const StatusPill: React.FC<{
  tone: 'olive' | 'sand' | 'muted';
  children: React.ReactNode;
}> = ({ tone, children }) => (
  <span
    className={cn(
      'col-label flex h-6 items-center gap-[7px] whitespace-nowrap rounded-full border px-2.5 tracking-[.12em]',
      tone === 'olive' && 'border-olive-line/40 bg-accent text-olive-ink',
      tone === 'sand' && 'border-sand/35 text-sand',
      tone === 'muted' && 'border-border text-muted-foreground',
    )}
  >
    <span className="size-1.5 rounded-full bg-current" />
    {children}
  </span>
);

/** Aviso tracejado em areia (tracejado = pendência, não dado). */
export const DashedAlert: React.FC<{
  icon: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, action, children }) => (
  <div className="mx-6 mb-[22px] flex items-center gap-3 rounded-xl border border-dashed border-sand/35 px-3.5 py-3">
    <span className="text-sand">{icon}</span>
    <span className="flex-1 text-[12.5px] text-muted-foreground">{children}</span>
    {action}
  </div>
);

/** Contador areia 22px ao lado do título do card de Pendências. */
export const CountBadge: React.FC<{ value: number }> = ({ value }) => (
  <span className="font-display flex h-[22px] min-w-[26px] items-center justify-center rounded-full bg-sand px-2 text-[11.5px] font-extrabold text-on-sand">
    {value}
  </span>
);

